// src/jsx/components/TenantSwitcher.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Dropdown, Form, InputGroup } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setTenant } from "../../../store/slices/tenantSlice";
import { fetchTenantTreeThunk } from "../../../store/slices/userPanchayathAssignSlice";
import {
  clearPermissions,
  fetchMyPermissionsThunk,
} from "../../../store/slices/permissionSlice";

const LS_DIST = "tenant_dist_id";
const LS_TALUK = "tenant_taluk_id";

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export default function TenantSwitcher() {
  const dispatch = useDispatch();
  const initRef = useRef(false);

  const token = useSelector((s) => s.auth?.token);
  const role = String(useSelector((s) => s.auth?.user?.role) || "").toLowerCase();
  const isSuperadmin = role === "superadmin";

  const tree = useSelector((s) => s.userPanAssign?.tenantTree || {});
  const districts = tree.districts || [];
  const taluks = tree.taluks || [];
  const panchayaths = tree.panchayaths || [];
  const linked_pan_ids = Array.isArray(tree.linked_pan_ids)
    ? tree.linked_pan_ids
    : [];

  const panIdsRedux = useSelector((s) => s.tenant?.pan_ids || []);
  const panActiveRedux = useSelector((s) => s.tenant?.pan_id_active || 0);
  const panNameRedux = useSelector(
    (s) => s.tenant?.pan_name || "Select Panchayath"
  );

  const [distId, setDistId] = useState(toNum(localStorage.getItem(LS_DIST) || 0));
  const [talukId, setTalukId] = useState(toNum(localStorage.getItem(LS_TALUK) || 0));
  const [talukSearch, setTalukSearch] = useState("");
  const [panSearch, setPanSearch] = useState("");

  useEffect(() => {
    if (!token) return;
    if (initRef.current) return;
    initRef.current = true;
    dispatch(fetchTenantTreeThunk());
  }, [token, dispatch]);

  const allowedSet = useMemo(() => {
    if (!token) return null;
    if (isSuperadmin) return null;

    const ids = (linked_pan_ids || []).map((x) => toNum(x)).filter(Boolean);
    return ids.length ? new Set(ids) : null;
  }, [token, isSuperadmin, linked_pan_ids]);

  const taluksFiltered = useMemo(() => {
    if (!token) return [];
    const list = !distId
      ? taluks
      : taluks.filter((t) => toNum(t.dist_id) === distId);

    const q = talukSearch.trim().toLowerCase();
    return q
      ? list.filter((t) =>
          String(t.taluk_name || "").toLowerCase().includes(q)
        )
      : list;
  }, [token, taluks, distId, talukSearch]);

  const pansFiltered = useMemo(() => {
    if (!token) return [];

    const q = panSearch.trim().toLowerCase();

    return (panchayaths || []).filter((p) => {
      const pid = toNum(p.pan_id);

      if (allowedSet && !allowedSet.has(pid)) return false;
      if (distId && toNum(p.dist_id) !== distId) return false;
      if (talukId && toNum(p.taluk_id) !== talukId) return false;

      if (q) {
        const name = String(p.pan_name || "").toLowerCase();
        const domain = String(p.tenant_domain || "").toLowerCase();
        if (!name.includes(q) && !domain.includes(q)) return false;
      }

      return true;
    });
  }, [token, panchayaths, allowedSet, distId, talukId, panSearch]);

  const activeDistrictName = useMemo(() => {
    if (!token) return "District";
    return (
      districts.find((d) => toNum(d.dist_id) === distId)?.dist_name || "District"
    );
  }, [token, districts, distId]);

  const activeTalukName = useMemo(() => {
    if (!token) return "Taluk";
    return taluks.find((t) => toNum(t.taluk_id) === talukId)?.taluk_name || "Taluk";
  }, [token, taluks, talukId]);

  const label = useMemo(() => {
    if (!token) return "Select Panchayath";
    if (panIdsRedux.length > 1) {
      return isSuperadmin ? "All Panchayaths" : "All Assigned Panchayaths";
    }
    return panNameRedux || "Select Panchayath";
  }, [token, panIdsRedux, isSuperadmin, panNameRedux]);

  const reloadPermissions = () => {
    dispatch(clearPermissions());
    dispatch(fetchMyPermissionsThunk());
  };

  const applySingle = (p) => {
    const id = toNum(p.pan_id);

    dispatch(
      setTenant({
        pan_id: id,
        pan_ids: [id],
        pan_id_active: id,
        pan_name: p.pan_name,
      })
    );

    reloadPermissions();
  };

  const applyAll = (list = pansFiltered) => {
    const ids = (list || []).map((p) => toNum(p.pan_id)).filter(Boolean);

    if (ids.length === 0) {
      dispatch(
        setTenant({
          pan_id: 0,
          pan_ids: [],
          pan_id_active: 0,
          pan_name: "Select Panchayath",
        })
      );
      reloadPermissions();
      return;
    }

    if (ids.length === 1) {
      const p = list[0];
      dispatch(
        setTenant({
          pan_id: toNum(p.pan_id),
          pan_ids: [toNum(p.pan_id)],
          pan_id_active: toNum(p.pan_id),
          pan_name: p.pan_name,
        })
      );
    } else {
      dispatch(
        setTenant({
          pan_id: 0,
          pan_ids: ids,
          pan_id_active: 0,
          pan_name: isSuperadmin
            ? "All Panchayaths"
            : "All Assigned Panchayaths",
        })
      );
    }

    reloadPermissions();
  };

  useEffect(() => {
    if (!token) return;
    if (!isSuperadmin) return;
    if (panIdsRedux.length > 0 || panActiveRedux > 0) return;
    if (!panchayaths.length) return;

    const allIds = panchayaths.map((p) => toNum(p.pan_id)).filter(Boolean);
    if (!allIds.length) return;

    dispatch(
      setTenant({
        pan_id: 0,
        pan_ids: allIds,
        pan_id_active: 0,
        pan_name: "All Panchayaths",
      })
    );

    reloadPermissions();
  }, [
    token,
    isSuperadmin,
    panchayaths,
    panIdsRedux.length,
    panActiveRedux,
    dispatch,
  ]);

  if (!token) return null;

  const handleDistrictChange = (d) => {
    const did = toNum(d.dist_id);

    setDistId(did);
    localStorage.setItem(LS_DIST, String(did));

    setTalukId(0);
    localStorage.setItem(LS_TALUK, "0");

    setTalukSearch("");
    setPanSearch("");

    const nextPans = (panchayaths || []).filter((p) => {
      const pid = toNum(p.pan_id);
      if (allowedSet && !allowedSet.has(pid)) return false;
      if (did && toNum(p.dist_id) !== did) return false;
      return true;
    });

    if (nextPans.length) {
      const ids = nextPans.map((p) => toNum(p.pan_id)).filter(Boolean);

      if (ids.length === 1) {
        const p = nextPans[0];
        dispatch(
          setTenant({
            pan_id: toNum(p.pan_id),
            pan_ids: [toNum(p.pan_id)],
            pan_id_active: toNum(p.pan_id),
            pan_name: p.pan_name,
          })
        );
      } else {
        dispatch(
          setTenant({
            pan_id: 0,
            pan_ids: ids,
            pan_id_active: 0,
            pan_name: isSuperadmin
              ? "All Panchayaths"
              : "All Assigned Panchayaths",
          })
        );
      }
    } else {
      dispatch(
        setTenant({
          pan_id: 0,
          pan_ids: [],
          pan_id_active: 0,
          pan_name: "Select Panchayath",
        })
      );
    }

    reloadPermissions();
  };

  const handleTalukChange = (t) => {
    const tid = toNum(t.taluk_id);

    setTalukId(tid);
    localStorage.setItem(LS_TALUK, String(tid));
    setPanSearch("");

    const nextPans = (panchayaths || []).filter((p) => {
      const pid = toNum(p.pan_id);
      if (allowedSet && !allowedSet.has(pid)) return false;
      if (distId && toNum(p.dist_id) !== distId) return false;
      if (tid && toNum(p.taluk_id) !== tid) return false;
      return true;
    });

    if (nextPans.length) {
      const ids = nextPans.map((p) => toNum(p.pan_id)).filter(Boolean);

      if (ids.length === 1) {
        const p = nextPans[0];
        dispatch(
          setTenant({
            pan_id: toNum(p.pan_id),
            pan_ids: [toNum(p.pan_id)],
            pan_id_active: toNum(p.pan_id),
            pan_name: p.pan_name,
          })
        );
      } else {
        dispatch(
          setTenant({
            pan_id: 0,
            pan_ids: ids,
            pan_id_active: 0,
            pan_name: isSuperadmin
              ? "All Panchayaths"
              : "All Assigned Panchayaths",
          })
        );
      }
    } else {
      dispatch(
        setTenant({
          pan_id: 0,
          pan_ids: [],
          pan_id_active: 0,
          pan_name: "Select Panchayath",
        })
      );
    }

    reloadPermissions();
  };

  return (
    <div className="tenantSwitch d-flex gap-2 align-items-center">
      <Dropdown align="end" autoClose="outside">
        <Dropdown.Toggle variant="light">{activeDistrictName} ▾</Dropdown.Toggle>
        <Dropdown.Menu className="tenant-menu">
          <Dropdown.Item
            active={distId === 0}
            onClick={() => handleDistrictChange({ dist_id: 0 })}
          >
            All Districts
          </Dropdown.Item>

          {districts.map((d) => (
            <Dropdown.Item
              key={d.dist_id}
              active={toNum(d.dist_id) === distId}
              onClick={() => handleDistrictChange(d)}
            >
              {d.dist_name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <Dropdown align="end" autoClose="outside">
        <Dropdown.Toggle variant="light">{activeTalukName} ▾</Dropdown.Toggle>
        <Dropdown.Menu className="tenant-menu">
          <InputGroup className="px-2 py-2">
            <Form.Control
              value={talukSearch}
              onChange={(e) => setTalukSearch(e.target.value)}
              placeholder="Search taluk…"
            />
          </InputGroup>

          <Dropdown.Item
            active={talukId === 0}
            onClick={() => handleTalukChange({ taluk_id: 0 })}
          >
            All Taluks
          </Dropdown.Item>

          {taluksFiltered.map((t) => (
            <Dropdown.Item
              key={t.taluk_id}
              active={toNum(t.taluk_id) === talukId}
              onClick={() => handleTalukChange(t)}
            >
              {t.taluk_name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <Dropdown align="end" autoClose="outside">
  <Dropdown.Toggle variant="light" className="tenant-toggle-wide">
    {label} ▾
  </Dropdown.Toggle>

<Dropdown.Menu className="tenant-menu tenant-menu-wide">
  <div className="tenant-menu-fixed">
    <div className="tenant-menu-head">
      <strong>Panchayath</strong>

      <Badge bg="warning" pill>
        {pansFiltered.length}
      </Badge>
    </div>

    <Dropdown.Item
      onClick={() => applyAll()}
      disabled={pansFiltered.length < 1}
    >
      ✅{" "}
      {isSuperadmin
        ? "All Panchayaths"
        : "All Assigned Panchayaths"}
    </Dropdown.Item>

    <Dropdown.Divider />

    <InputGroup>
      <Form.Control
        className="tenant-search"
        value={panSearch}
        onChange={(e) => setPanSearch(e.target.value)}
        placeholder="Search panchayath..."
      />
    </InputGroup>
  </div>

  <div className="tenant-panchayath-scroll">
    {pansFiltered.map((p) => (
      <Dropdown.Item
        key={p.pan_id}
        active={toNum(p.pan_id) === toNum(panActiveRedux)}
        onClick={() => applySingle(p)}
      >
        {p.pan_name}
      </Dropdown.Item>
    ))}

    {pansFiltered.length === 0 && (
      <div className="px-3 py-3 text-muted small">
        No panchayath found
      </div>
    )}
  </div>
</Dropdown.Menu>
</Dropdown>
    </div>
  );
}